import { useEffect, useState } from "react"
import { FaCircleCheck } from "react-icons/fa6"
import { useNavigate } from "react-router-dom"
import { Box, Center, HStack, RadioGroup, Separator, Spacer, Stack, VStack } from "@chakra-ui/react"
import { Button, Text, TextField } from "@/components"
import { useAuth, useViewer } from "@/contexts"
import { useClientContactInfo, useInsertBc } from "@/hooks"
import { Deal, DealStep } from "@/types"
import { BcOnboardingComponent } from "./BcOnboardingComponent"

interface Props {
  currentDeal: Deal
}

export const BackgroundCheckFormComponent = ({ currentDeal }: Props) => {
  const navigate = useNavigate()
  const yesNoItems = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ]

  const { user } = useAuth()
  const { viewedClient } = useViewer()
  const { data: contactInfo } = useClientContactInfo()

  const [legalName, setLegalName] = useState<string>("")
  const [preferredEmail, setPreferredEmail] = useState<string>("")
  const [address, setAddress] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [state, setState] = useState<string>("")
  const [zipCode, setZipCode] = useState<string>("")
  const [country, setCountry] = useState<string>("")
  const [phoneCountry, setPhoneCountry] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")

  useEffect(() => {
    const client = viewedClient ?? (user?.userType === "client" ? user : null)
    if (client) {
      setLegalName(`${client.firstName} ${client.lastName}`)
      setPreferredEmail(client.email)
      if (client.phoneNumber) setPhoneNumber(client.phoneNumber)
    }
  }, [user, viewedClient])

  useEffect(() => {
    if (contactInfo) {
      if (contactInfo.address) setAddress(contactInfo.address)
      if (contactInfo.city) setCity(contactInfo.city)
      if (contactInfo.state) setState(contactInfo.state)
      if (contactInfo.zip) setZipCode(contactInfo.zip)
      if (contactInfo.phone && !phoneNumber) setPhoneNumber(contactInfo.phone)
    }
  }, [contactInfo])

  const [ssn, setSsn] = useState<string>("")
  const [birthdate, setBirthdate] = useState<string>("")
  const [isUsCitizen, setIsUsCitizen] = useState<string>("")
  const [isMarried, setIsMarried] = useState<string>("")
  const [spouseName, setSpouseName] = useState<string>("")
  const [spouseEmail, setSpouseEmail] = useState<string>("")
  
  const { mutateAsync: insertBcInfo, isPending } = useInsertBc()
  const [hasSubmitted, setHasSubmitted] = useState(false)

  if (!user) return null

  const handleSubmit = async () => {
    if (user.userType !== "client") return null
    try {
      if (!legalName || !preferredEmail || !address || !city || !state || !zipCode || !phoneNumber || !ssn || !birthdate || !isUsCitizen || !isMarried) {
        alert("Please fill out all required fields.")
        return
      }

      const bcData = {
        dealId: currentDeal.id,
        legalName,
        email: preferredEmail,
        address,
        city,
        state,
        zip: zipCode,
        country: country || phoneCountry || undefined,
        phone: phoneNumber,
        ssn,
        birthDate: birthdate,
        isUsCitizen,
        isMarried,
        spouseName,
        spouseEmail,
        dealManagerName: `${user.dealManager.firstName} ${user.dealManager.lastName}`,
        dealManagerEmail: user.dealManager.email
      }

      await insertBcInfo(bcData)
      setHasSubmitted(true)
      // Clear sensitive fields immediately after successful submission
      setSsn("")
      setBirthdate("")
    } catch (error) {
      console.error("Validation error:", error)
      return
    }
  }

  if (hasSubmitted) {
    return <BcOnboardingComponent />
  }

  if (
    currentDeal.currentStep === DealStep.SIGN_BC ||
    currentDeal.currentStep === DealStep.UPLOAD_CLOSING_DOCS ||
    currentDeal.currentStep === DealStep.CLOSING
  ) {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "40%" }} p={5} borderRadius={10} bg="white" boxShadow="md" mt={20} >
          <Box borderRadius={5} bg="green.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center" mt={2} mb={2}>
            Thank You!
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Thank you for signing our Background Check Form.
            Please check your email for a DocuSign link sent from docusign@esofund.com which you and your Equity Partner will sign.
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Don't forget to check your email for a DocuSign link sent from docusign@esofund.com which you and your Equity Partner will sign.
            In the meantime, you can go to the documents page to upload any files you still need to upload.
            If you have any questions, feel free to email or schedule a call with your equity partner!
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Note that this form cannot be accepted unless it has your full legal name (including middle names), SSN, your current home address (no PO box), and birthdate.
            Also, be sure to lift any security freeze on your credit file (all three bureaus) for at least a day so we can run the report.
          </Text>
          <Text fontSize="sm" textAlign="center">
            After the credit check, you might receive unsolicited financing offers from third parties. Please note that this is due to credit bureau marketing efforts, and not because ESO has shared your information. To avoid this, opt out of the credit bureau marketing efforts by visiting OptOutPreScreen.com or calling 1-888-OPT-OUT (1-888-567-8688).
          </Text>
          <Button variant="solid" onClick={() => navigate(viewedClient ? `/clients/${viewedClient.id}` : "/documents")}>
            Proceed to Documents
          </Button>
        </VStack>
      </Center>
    )
  }

  return (
    <Stack as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSubmit() }} w="100%" h="100%" bg="white" p={{ base: 5, md: 10 }} borderLeftRadius={{ base: 0, md: 20 }} overflowY="scroll">
      <Text fontSize="md" mb={5}>
        Once submitted, you will automatically receive our e-signable credit and background check permission form via DocuSign for electronic signature.
      </Text>
      <Text>
        Note that this form cannot be accepted unless it has your full legal name (including middle names), SSN, your current home address (no PO box), and birthdate.
      </Text>
      <Text>
        Also, be sure to lift any security freeze on your credit file (all three bureaus) for at least a day so we can run the report.
      </Text>
      <Text>
        After the credit check, you might receive unsolicited financing offers from third parties. Please note that this is due to credit bureau marketing efforts, and not because ESO has shared your information. To avoid this, opt out of the credit bureau marketing efforts by visiting OptOutPreScreen.com or calling 1-888-OPT-OUT (1-888-567-8688).
      </Text>
      <Separator mt={5} mb={5} />
      <Stack gap={10}>
        <Stack direction={{ base: "column", md: "row" }} gap={4} mt={4}>
          <Stack gap={1}>
            <TextField
              label="Full Legal Name (exactly as it appears on your government ID)"
              placeholder="e.g. Edward Jordan Smith"
              type="text"
              name="name"
              autoComplete="name"
              value={legalName}
              onChange={(e) => setLegalName(e)}
            />
            <Text fontSize="xs" color="gray.500">
              This must match your government-issued ID exactly — including all middle names. The DocuSign sent after this form uses ID verification, so a nickname or shortened name (e.g. "Ed Smith" instead of "Edward Jordan Smith") will cause it to fail.
            </Text>
          </Stack>
          <TextField
            label="Email"
            placeholder="Enter your preferred email"
            name="email"
            autoComplete="email"
            value={preferredEmail}
            onChange={(e) => setPreferredEmail(e)}
          />
        </Stack>

        <TextField
          label="Address"
          placeholder="Enter your address"
          type="text"
          name="street-address"
          autoComplete="street-address"
          value={address}
          onChange={(e) => setAddress(e)}
        />

        <TextField
          label="City"
          placeholder="Enter your city"
          type="text"
          name="address-level2"
          autoComplete="address-level2"
          value={city}
          onChange={(e) => setCity(e)}
        />

        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <TextField
            label="State"
            placeholder="Enter your state"
            type="text"
            name="address-level1"
            autoComplete="address-level1"
            value={state}
            onChange={(e) => setState(e)}
          />
          <TextField
            label="Zip Code"
            placeholder="Enter your zip code"
            type="text"
            name="postal-code"
            autoComplete="postal-code"
            value={zipCode}
            onChange={(e) => setZipCode(e)}
          />
        </Stack>

        <TextField
          label="Country"
          placeholder="Enter your country"
          type="text"
          name="country-name"
          autoComplete="country-name"
          value={country}
          onChange={(e) => setCountry(e)}
        />

        <TextField
          label="Phone Number"
          placeholder="Enter your phone number"
          type="phone"
          name="tel"
          autoComplete="tel"
          value={phoneNumber}
          required
          onChange={(e) => setPhoneNumber(e)}
          onPhoneCountryChange={setPhoneCountry}
        />

        <Stack direction={{ base: "column", md: "row" }} gap={4}>
          <TextField
            label="Social Security Number (SSN)"
            placeholder="Enter your SSN"
            type="ssn"
            name="ssn"
            autoComplete="off"
            value={ssn}
            onChange={(e) => setSsn(e)}
          />

          <TextField
            label="Birthdate"
            placeholder="Enter your birthdate"
            type="date"
            name="bday"
            autoComplete="bday"
            value={birthdate}
            onChange={(e) => setBirthdate(e)}
          />
        </Stack>
      </Stack>
      <Separator mt={5} mb={5} />
      <Stack direction={{ base: "column", md: "row" }} gap={{ base: 6, md: 20 }}>
        <Stack>
          <Text fontSize="sm" fontWeight="bold">
            Are you a U.S. Citizen?
          </Text>
          <RadioGroup.Root defaultValue="1">
            <HStack gap="6">
              {yesNoItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value} onClick={() => setIsUsCitizen(item.value)}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </HStack>
          </RadioGroup.Root>
        </Stack>
        
        <Stack>
          <Text fontSize="sm" fontWeight="bold">
            Are you married?
          </Text>
          <RadioGroup.Root defaultValue="1">
            <HStack gap="6">
              {yesNoItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value} onClick={() => setIsMarried(item.value)}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </HStack>
          </RadioGroup.Root>
        </Stack>
      </Stack>

      {
        isMarried === "yes" && (
          <Stack direction={{ base: "column", md: "row" }} mt={5} gap={4}>
            <TextField
              label="Spouse Name"
              placeholder="Enter your spouse's name"
              type="text"
              value={spouseName}
              onChange={(e) => setSpouseName(e)}
            />

            <TextField
              label="Spouse Email"
              placeholder="Enter your spouse's email"
              type="text"
              value={spouseEmail}
              onChange={(e) => setSpouseEmail(e)}
            />
          </Stack>
        )
      }

      <HStack>
        <Spacer />
        <Button
          type="submit"
          variant="solid"
          w="fit-content"
          mt={5}
          loading={isPending}
          disabled={isPending}
        >
          Submit
        </Button>
      </HStack>
    </Stack>
  )
}
